import { isDefined } from "@clipboard-health/util-ts";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { type Worker } from "@src/appV2/Worker/api/types";
import { first } from "lodash";

import { INTERVIEW_STATUS_PRIORITY } from "../../constants";
import { getPlacementDetailsPath } from "../../paths";
import { type PlacementData } from "../../types/placement";
import { ApplicationPlacementCard } from "./ApplicationPlacementCard";
import { InterviewPlacementCard } from "./InterviewPlacementCard";

interface AppliedPlacementCardProps {
  placement: PlacementData;
  placementCandidate: Pick<PlacementCandidate, "id">;
  pageIndex: number;
  placementIndex: number;
  source: "booked_interviews" | "sent_applications" | "easy_scheduling_placements";
  worker: Pick<Worker, "tmz">;
}

export function AppliedPlacementCard(props: AppliedPlacementCardProps) {
  const { placement, placementCandidate, pageIndex, placementIndex, source, worker } = props;

  const interview = INTERVIEW_STATUS_PRIORITY.map((status) =>
    placement?.interviews?.find((interview) => interview.attributes.status === status)
  ).find((foundInterview) => foundInterview !== undefined);

  const application = first(placement.applications);

  const autoOpenInterviewBooking = source === "easy_scheduling_placements";
  const detailsPath = getPlacementDetailsPath(placement.id, placementCandidate.id, {
    autoOpenInterviewBooking,
  });

  if (isDefined(interview)) {
    return (
      <InterviewPlacementCard
        interview={{
          start: new Date(interview.attributes.start),
          end: new Date(interview.attributes.end),
          status: interview.attributes.status,
          ...(interview.attributes.scheduleStatus && {
            scheduleStatus: interview.attributes.scheduleStatus,
          }),
          isBanExcused: false,
        }}
        placement={placement}
        distance={placement.distance ?? 0}
        workplaceName={placement.workplace.name}
        pageIndex={pageIndex}
        cardIndex={placementIndex}
        placementCandidateId={placementCandidate.id}
        workerTimeZone={worker.tmz}
        placementDetailsPath={detailsPath}
        source={source}
      />
    );
  }

  if (isDefined(application)) {
    return (
      <ApplicationPlacementCard
        application={{
          appliedDate: application.attributes.appliedDate,
          updatedAt: application.attributes.updatedAt,
        }}
        placement={placement}
        placementCandidateId={placementCandidate.id}
        workplaceName={placement.workplace.name}
        pageIndex={pageIndex}
        placementIndex={placementIndex}
        source={source}
        workerTimeZone={worker.tmz}
      />
    );
  }

  return null;
}
